import { Star } from "@phosphor-icons/react";
import { fileUrl } from "@/lib/site";
import { Credit } from "@/components/demo/DemoHero";

const NOTE = "Exemplos — o restaurante confirma a carta e os preços";

export function Title({ kicker, children }) {
  return (
    <div className="mb-6">
      {kicker && <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.25em]" style={{ color: "var(--k)" }}>{kicker}</p>}
      <h2 className="text-3xl font-bold leading-tight">{children}</h2>
    </div>
  );
}

export function WhyCome({ items }) {
  if (!items?.length) return null;
  return (
    <section className="px-6 py-14" data-testid="demo-why">
      <Title kicker="Porque vir cá">O que dizem que é especial</Title>
      <ol className="space-y-6">
        {items.map((w, i) => (
          <li key={w.title} className="flex gap-4" data-testid={`demo-why-${i}`}>
            <span className="text-3xl font-bold leading-none" style={{ fontFamily: "var(--fh)", color: "var(--k)" }}>{String(i + 1).padStart(2, "0")}</span>
            <div><h3 className="text-xl font-semibold">{w.title}</h3><p className="mt-1 text-[15px] leading-relaxed" style={{ color: "var(--muted)" }}>{w.text}</p></div>
          </li>
        ))}
      </ol>
    </section>
  );
}

export function Dishes({ dishes }) {
  if (!dishes?.length) return null;
  return (
    <section className="py-14" data-testid="demo-dishes">
      <div className="px-6"><Title kicker="Pratos da casa">Para provar</Title></div>
      <div className="dz-snap -mt-2 flex gap-4 overflow-x-auto px-6 pb-2">
        {dishes.map((d, i) => (
          <figure key={d.photo.id} className={`dz-tile relative shrink-0 overflow-hidden ${dishes.length === 1 ? "w-full sm:w-[60%]" : "w-[76%] sm:w-[46%]"}`} style={{ borderRadius: "var(--r)" }} data-testid={`demo-dish-${i}`}>
            <img src={fileUrl(d.photo.thumb_id)} alt={d.name} loading="lazy" decoding="async" className="dz-img aspect-[4/5] w-full object-cover" />
            <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(0,0,0,.72), transparent 55%)" }} />
            <Credit c={d.photo.credit} className="absolute right-2 top-2 rounded-full bg-black/40 px-2 py-1" />
            <figcaption className="absolute inset-x-0 bottom-0 p-4 text-white">
              <h3 className="text-xl font-semibold leading-tight">{d.name}</h3>
              {d.description && <p className="mt-1 text-sm text-white/85">{d.description}</p>}
            </figcaption>
          </figure>
        ))}
      </div>
      <p className="mt-3 px-6 text-xs italic" style={{ color: "var(--muted)" }} data-testid="demo-dishes-note">{NOTE}</p>
    </section>
  );
}

export function Carta({ menu, source, note }) {
  if (!menu?.length) return null;
  return (
    <section className="px-6 py-14" data-testid="demo-menu">
      <Title kicker="Carta">O que há na casa</Title>
      <ul className="dz-card divide-y" style={{ borderColor: "var(--line)" }}>
        {menu.slice(0, 10).map((i) => (
          <li key={i.id} className="flex items-baseline justify-between gap-3 p-4" style={{ borderColor: "var(--line)" }}>
            <div><h3 className="font-semibold" style={{ fontFamily: "var(--fb)" }}>{i.name}</h3>{i.description && <p className="mt-0.5 text-sm" style={{ color: "var(--muted)" }}>{i.description}</p>}</div>
            {i.price > 0 && <span className="whitespace-nowrap text-sm font-bold" style={{ color: "var(--p)" }}>{i.price} CVE</span>}
          </li>
        ))}
      </ul>
      {(source === "avaliacoes" || note) && <p className="mt-3 text-xs italic" style={{ color: "var(--muted)" }} data-testid="demo-menu-note">{NOTE}</p>}
    </section>
  );
}

export function Gallery({ items }) {
  if (!items?.length) return null;
  return (
    <section className="px-6 py-14" data-testid="demo-gallery">
      <Title kicker="Galeria">O ambiente</Title>
      <div className="grid auto-rows-[130px] grid-cols-2 gap-2 sm:auto-rows-[180px] sm:grid-cols-3">
        {items.slice(0, 8).map((g, i) => (
          <a key={g.id} href={fileUrl(g.id)} target="_blank" rel="noreferrer" data-testid={`demo-gallery-${i}`}
            className={`dz-tile relative overflow-hidden ${i % 5 === 0 ? "col-span-2 row-span-2" : ""}`} style={{ borderRadius: "calc(var(--r) * .6)" }}>
            <img src={fileUrl(g.thumb_id)} alt="" loading="lazy" decoding="async" className="dz-img h-full w-full object-cover" />
            <Credit c={g.credit} className="absolute bottom-1.5 left-1.5 rounded-full bg-black/40 px-2 py-1" />
          </a>
        ))}
      </div>
    </section>
  );
}

export function Reviews({ s }) {
  if (!s.google_reviews?.length) return null;
  return (
    <section className="px-6 py-14" data-testid="demo-reviews">
      <Title kicker={s.from_google ? "Avaliações Google" : "Avaliações"}>Quem já veio</Title>
      <div className="dz-snap -mx-6 flex gap-3 overflow-x-auto px-6">
        {s.google_reviews.map((r, i) => (
          <blockquote key={i} className="dz-card w-[82%] shrink-0 p-5 sm:w-[45%]" data-testid={`demo-review-${i}`}>
            <div className="flex gap-0.5">{Array.from({ length: Math.round(r.rating || 5) }).map((_, k) => <Star key={k} size={14} weight="fill" className="text-amber-500" />)}</div>
            <p className="dz-clamp3 mt-3 text-[15px] leading-relaxed">“{r.text}”</p>
            <footer className="mt-3 text-xs" style={{ color: "var(--muted)" }} data-testid={`demo-review-author-${i}`}>
              {r.author_uri ? <a href={r.author_uri} target="_blank" rel="noreferrer" className="font-semibold underline-offset-2 hover:underline">{r.author}</a> : <b>{r.author}</b>}
              {r.via ? ` · via ${r.via}` : ""}{r.time ? ` · ${r.time}` : ""}
            </footer>
          </blockquote>
        ))}
      </div>
    </section>
  );
}
